import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authService } from '../../services/auth/authService';
import { DashboardHeader } from '../../components/layout/DashboardHeader/DashboardHeader';
import { DashboardSidebar } from '../../components/layout/DashboardSidebar/DashboardSidebar';
import { createSidebarItems } from '../../components/layout/DashboardSidebar/sidebarItems';
import { PatientMealPlan } from '../../services/patients/patientsService';
import './PatientDetailPage.css';

export interface BreadcrumbSegment {
  label: string;
  to?: string;
}

export interface DietPlanLayoutProps {
  patientId: string;
  goalId: string;
  patientName: string | null;
  mealPlan: PatientMealPlan | null;
  breadcrumbSegments: BreadcrumbSegment[];
  pageTitle: React.ReactNode;
  children: React.ReactNode;
  loading?: boolean;
  error?: string | null;
  /** Optional content to show on the right of the page title (e.g. "Back to list") */
  titleRight?: React.ReactNode;
  /** Label before the effective date; default "Effective:" */
  effectiveLabel?: string;
  /** Optional control block on the right side of diet-plan meta card (e.g. version selector) */
  metaRight?: React.ReactNode;
  /** Compact 3-column meta layout used on meal plan view screen */
  compactMetaColumns?: boolean;
  /** Hide top diet-plan meta card (when page renders unified custom card) */
  hideMetaCard?: boolean;
}

/**
 * Shared layout for meal plan pages under a diet plan: list, create, view, edit.
 * Renders dashboard shell, breadcrumb (Patients > Patient > Diet Plan > …), diet plan summary card, and page title.
 */
const DietPlanLayout: React.FC<DietPlanLayoutProps> = ({
  patientId,
  goalId,
  patientName,
  mealPlan,
  breadcrumbSegments,
  pageTitle,
  children,
  loading = false,
  error = null,
  titleRight,
  effectiveLabel = 'Effective:',
  metaRight,
  compactMetaColumns = false,
  hideMetaCard = false,
}) => {
  const navigate = useNavigate();

  const sidebarContext = {
    patientId,
    patientName: patientName ?? undefined,
    goalId,
    mealPlanName: mealPlan?.goalName ?? undefined,
    mealPlanSection: 'daily-meals' as const,
  };
  const sidebarItems = createSidebarItems(navigate, window.location.pathname, sidebarContext);

  const handleLogout = () => {
    authService.clearTokens();
    navigate('/login');
  };

  const handleGoToProfile = () => {
    navigate('/profile');
  };

  if (loading) {
    return (
      <div className="dashboard">
        <DashboardHeader onProfileClick={handleGoToProfile} onLogout={handleLogout} />
        <div className="dashboard-layout">
          <DashboardSidebar items={sidebarItems} />
          <div className="dashboard-container">
            <main className="dashboard-main-content">
              <div className="loading-message">Loading...</div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  if (error || !mealPlan) {
    return (
      <div className="dashboard">
        <DashboardHeader onProfileClick={handleGoToProfile} onLogout={handleLogout} />
        <div className="dashboard-layout">
          <DashboardSidebar items={sidebarItems} />
          <div className="dashboard-container">
            <main className="dashboard-main-content">
              <div className="error-message">Error: {error || 'Diet plan not found'}</div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <DashboardHeader onProfileClick={handleGoToProfile} onLogout={handleLogout} />
      <div className="dashboard-layout">
        <DashboardSidebar items={sidebarItems} />
        <div className="dashboard-container">
          <main className="dashboard-main-content">
            <div className="section-card">
              <div className="meal-plan-content">
                <nav className="detail-breadcrumb" aria-label="Breadcrumb">
                  <Link to="/patients" className="breadcrumb-link">Patients</Link>
                  <span className="breadcrumb-sep" aria-hidden>›</span>
                  <Link to={`/patients/${patientId}`} className="breadcrumb-link">{patientName ?? 'Patient'}</Link>
                  <span className="breadcrumb-sep" aria-hidden>›</span>
                  <Link to={`/patients/${patientId}/diet-plans`} className="breadcrumb-link">Diet Plans</Link>
                  <span className="breadcrumb-sep" aria-hidden>›</span>
                  <Link to={`/patients/${patientId}/diet-plan/${goalId}/daily-nutrition`} className="breadcrumb-link">{mealPlan?.goalName ?? 'Diet Plan'}</Link>
                  {breadcrumbSegments.map((seg, i) => (
                    <React.Fragment key={i}>
                      <span className="breadcrumb-sep" aria-hidden>›</span>
                      {seg.to != null ? (
                        <Link to={seg.to} className="breadcrumb-link">{seg.label}</Link>
                      ) : (
                        <span className="breadcrumb-current">{seg.label}</span>
                      )}
                    </React.Fragment>
                  ))}
                </nav>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
                  <h1 className="meal-plan-page-title" style={{ margin: 0 }}>{pageTitle}</h1>
                  {titleRight != null ? titleRight : null}
                </div>
                {!hideMetaCard && (
                <div className="meal-plan-detail-meta-card">
                  <span className="meal-plan-detail-meta-label">Diet Plan</span>
                  {compactMetaColumns ? (
                    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1fr) auto auto', alignItems: 'center', gap: 0 }}>
                      <div style={{ paddingRight: 18 }}>
                        <span className="meal-plan-detail-meta-name">{mealPlan.goalName}</span>
                      </div>
                      <div style={{ borderLeft: '1px solid #e5e7eb', padding: '0 18px' }}>
                        <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          {effectiveLabel.replace(':', '')}
                        </div>
                        <div style={{ fontSize: '1rem', fontWeight: 600, color: '#111827', marginTop: 2 }}>
                          {mealPlan.effectiveAt ? new Date(mealPlan.effectiveAt).toLocaleDateString() : '—'}
                        </div>
                      </div>
                      <div style={{ borderLeft: '1px solid #e5e7eb', paddingLeft: 18 }}>
                        {metaRight != null ? metaRight : null}
                      </div>
                    </div>
                  ) : (
                    <div className="meal-plan-detail-meta-line">
                      <span className="meal-plan-detail-meta-name">{mealPlan.goalName}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap', marginLeft: 'auto' }}>
                        <span className="meal-plan-detail-meta-date">
                          {effectiveLabel} {mealPlan.effectiveAt ? new Date(mealPlan.effectiveAt).toLocaleDateString() : '—'}
                        </span>
                        {metaRight != null ? metaRight : null}
                      </div>
                    </div>
                  )}
                </div>
                )}
                {children}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
};

export default DietPlanLayout;
